/*
 * Step: product.lumi (/product/lumi), last in Counsel. The AI chat, complementary
 * to the Smart Report: it has already read the patient's results and answers in
 * their own numbers (Harsh, 2026-10-17). Shown as "AI chat" everywhere; the name
 * Lumi appears once, in the line under the headline (meeting 2026-10-20).
 *
 * Left: Harsh's 60 s explainer (9:16) in a phone frame. It never plays by
 * itself. One always-mounted button, product.lumi.play, toggles it: "Play the
 * AI chat demo (0:41)" over the poster while paused, a small "Pause the AI chat
 * demo" in the corner while playing (Q12: same element, label changes, no
 * aria-pressed). The native controls appear once it has started. The player's
 * data-trapezium-state follows the video's own play / pause events
 * (playing | paused). product.lumi.maximize sits in the frame's top-left corner.
 * Right: four big blocks, the four things the video shows, carry the message.
 */
import { useCallback, useRef, useState } from 'react';
import { FileText, ListChecks, MessageCircle, MessagesSquare, Mic, Pause, Play } from 'lucide-react';
import Screen from '../../demo/Screen';
import VideoMaximize from '../../ui/VideoMaximize';
import { ProductChip } from './ProductDetail';
import './Lumi.css';

const VIDEO = {
  src: '/demo/video/lumi-explainer.mp4',
  poster: '/demo/video/lumi-explainer.jpg',
  title: 'AI chat, complementary to the Smart Report (0:41)',
};

const POINTS = [
  { key: 'reads', icon: FileText, tone: 'blue', title: 'Reads your report', text: 'Nothing to type in.' },
  { key: 'plain', icon: MessageCircle, tone: 'teal', title: 'Plain-language answers', text: 'In their own numbers.' },
  { key: 'follow-up', icon: ListChecks, tone: 'amber', title: 'Follow-up questions', text: 'And what to ask the doctor.' },
  { key: 'voice', icon: Mic, tone: 'purple', title: 'Speak instead of typing', text: 'Just ask out loud.' },
];

function Player() {
  const videoRef = useRef(null);
  const [video, setVideo] = useState(null);
  const [state, setState] = useState('paused');
  const [started, setStarted] = useState(false);

  const playing = state === 'playing';

  const attach = useCallback((node) => {
    videoRef.current = node;
    setVideo(node);
  }, []);

  const toggle = useCallback(() => {
    const el = videoRef.current;
    if (!el) return;
    if (playing) {
      el.pause();
      return;
    }
    setStarted(true);
    const request = el.play();
    if (request && typeof request.catch === 'function') request.catch(() => setState('paused'));
  }, [playing]);

  return (
    <div className="lumi-player" data-trapezium-state={state}>
      <div className="lumi-phone">
        <span className="lumi-phone__notch" aria-hidden="true" />
        <video
          ref={attach}
          className="lumi-phone__video"
          src={VIDEO.src}
          poster={VIDEO.poster}
          preload="metadata"
          muted /* the cut has no audio track (client, 2026-10-24) */
          playsInline
          controls={started}
          aria-label={VIDEO.title}
          onPlay={() => {
            setStarted(true);
            setState('playing');
          }}
          onPause={() => setState('paused')}
          onEnded={() => setState('paused')}
        />
        <VideoMaximize video={video} id="product.lumi.maximize" label="the AI chat demo" className="lumi-max" />
        {/* Always mounted: an overlay while paused, a small corner control while playing. */}
        <button
          type="button"
          className={playing ? 'lumi-play is-playing' : 'lumi-play'}
          data-trapezium-id="product.lumi.play"
          onClick={toggle}
        >
          {playing ? (
            <>
              <Pause size={14} strokeWidth={2.6} fill="currentColor" aria-hidden="true" />
              <span className="lumi-play__label">
                Pause <span className="sr-only">the AI chat demo</span>
              </span>
            </>
          ) : (
            <>
              <span className="lumi-play__disc" aria-hidden="true">
                <Play size={26} strokeWidth={2.4} fill="currentColor" />
              </span>
              <span className="lumi-play__label">
                Play the AI chat demo <span className="lumi-play__time">(0:41)</span>
              </span>
            </>
          )}
        </button>
      </div>
      <p className="lumi-player__note">41 seconds · no sound</p>
    </div>
  );
}

export default function Lumi() {
  return (
    <Screen id="product.lumi" title="AI chat" className="lumi">
      <div className="lumi-hero">
        <Player />

        <div className="lumi-copy">
          <ProductChip icon={MessagesSquare} name="AI chat" stage="Counsel" tone="counsel" />
          <h1 className="headline pd-headline lumi-headline">
            Answers every question <em>about their report.</em>
          </h1>
          <p className="lede pd-lede lumi-lede">Meet Lumi, the AI chat complementary to the Smart Report.</p>

          <ul className="lumi-points">
            {POINTS.map(({ key, icon: Icon, tone, title, text }) => (
              <li key={key} className="lumi-point">
                <span className={`icon-tile icon-tile--${tone} lumi-point__icon`} aria-hidden="true">
                  <Icon size={34} strokeWidth={2.1} />
                </span>
                <strong>{title}</strong>
                <span>{text}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </Screen>
  );
}
